import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { TradingDataService } from '../../../../services/trading-data.service';
import { SymbolDetails } from '../../../../types/symbol.types';
import { SpotAccountData } from '../../../../types/funds.types';

interface LimitOrderFormProps {
  tradeType: 'buy' | 'sell';
  baseCurrency: string;
  quoteCurrency: string;
  symbolDetails: SymbolDetails | null;
  accountBalance: SpotAccountData | null;
  availableQty: string;
  currentPrice: any;
  price: string;
  quantity: string;
  orderValue: string;
  sliderPercentage: number;
  errors: {
    price?: string;
    quantity?: string;
    orderValue?: string;
    insufficientBalance?: string;
  };
  setPrice: (value: string) => void;
  setQuantity: (value: string) => void;
  setOrderValue: (value: string) => void;
  setSliderPercentage: (value: number) => void;
  setErrors: (errors: any) => void;
  handlePriceChange: (value: string) => void;
  handleQuantityChange: (value: string) => void;
  handleOrderValueChange: (value: string) => void;
  handleSliderChange: (value: number) => void;
  validatePrice: (value: string) => string | undefined;
  validateQuantity: (value: string) => string | undefined;
  validateOrderValue: (value: string) => string | undefined;
  MIN_PRICE_ORDER_IN_USD: number;
  ORDER_VALUE_PRECISION: number;
}

const LimitOrderForm = ({
  tradeType,
  baseCurrency,
  quoteCurrency,
  symbolDetails,
  accountBalance,
  availableQty,
  currentPrice,
  price,
  quantity,
  orderValue,
  sliderPercentage,
  errors,
  setPrice,
  setQuantity,
  setOrderValue,
  setSliderPercentage,
  setErrors,
  handlePriceChange,
  handleQuantityChange,
  handleOrderValueChange,
  handleSliderChange,
  validatePrice,
  validateQuantity,
  validateOrderValue,
  MIN_PRICE_ORDER_IN_USD,
  ORDER_VALUE_PRECISION
}: LimitOrderFormProps) => {
  // Brief spin so the refresh control always acknowledges the click, even when the
  // market price is unchanged and the input value therefore stays the same.
  const [isSpinning, setIsSpinning] = useState(false);

  // Normalised latest market price from the live chart feed, or null if not warm yet.
  const latestPrice =
    currentPrice?.value !== undefined &&
    currentPrice?.value !== null &&
    currentPrice?.value !== '' &&
    Number.isFinite(Number(currentPrice.value))
      ? currentPrice.value.toString()
      : null;

  const dataServiceRef = useRef<TradingDataService | null>(null);

  // Pull the newest price into the Price field. Prefers the live websocket value,
  // and falls back to fetching the latest candle so the control still works before
  // (or without) the realtime feed being connected.
  const refreshPrice = async () => {
    setIsSpinning(true);
    try {
      if (latestPrice !== null) {
        handlePriceChange(latestPrice);
        return;
      }
      if (!dataServiceRef.current) {
        dataServiceRef.current = new TradingDataService();
      }
      const candles = await dataServiceRef.current.getCandleData(
        `${baseCurrency}-${quoteCurrency}`,
        '1min'
      );
      const close = candles?.[candles.length - 1]?.close;
      if (typeof close === 'number' && Number.isFinite(close)) {
        handlePriceChange(close.toString());
      }
    } catch {
      // Leave whatever the user typed untouched if the fetch fails.
    } finally {
      window.setTimeout(() => setIsSpinning(false), 400);
    }
  };

  return (
    <>
      {/* Price Input */}
      <div className="relative">
        <div className="flex items-center gap-1">
          <label className="text-f12 text-gray-400">Price</label>
          <button
            type="button"
            title={
              latestPrice !== null
                ? `Use latest market price (${latestPrice})`
                : 'Fetch latest market price'
            }
            aria-label="Use latest market price"
            disabled={isSpinning}
            onClick={refreshPrice}
            className="inline-flex items-center justify-center h-5 w-5 rounded text-gray-400 hover:text-white hover:bg-white/10 active:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <FontAwesomeIcon
              icon={faArrowsRotate}
              className={`scale-90 ${isSpinning ? 'animate-spin' : ''}`}
            />
          </button>
        </div>
        <input
          type="text"
          value={price}
          onChange={(e) => handlePriceChange(e.target.value)}
          className={`w-full dark:bg-black dark:text-white text-sm rounded px-3 py-2 mt-1 outline-none border ${errors.price ? '!border-red-500' : '!border-transparent'}`}
        />
        <div className="text-f12 dark:text-white mt-1 absolute bottom-[7px] right-3">
          {quoteCurrency}
        </div>
      </div>
      {errors.price && (
        <div className="text-red-500 text-f12 mt-1">{errors.price}</div>
      )}

      {/* Quantity Input */}
      <div className="relative">
        <label className="text-f12 text-gray-400">Quantity</label>
        <input
          type="text"
          value={quantity}
          onChange={(e) => handleQuantityChange(e.target.value)}
          className={`w-full dark:bg-black dark:text-white text-sm rounded px-3 py-2 mt-1 outline-none border ${errors.quantity ? '!border-red-500' : '!border-transparent'}`}
          placeholder="Quantity"
        />
        <div className="text-f12 dark:text-white  absolute bottom-[7px] right-3">
          {baseCurrency}
        </div>
      </div>
      {errors.quantity && (
        <div className="text-red-500 text-f12 mt-1">{errors.quantity}</div>
      )}

      {/* Slider */}
      <div className="flex items-center space-x-2 text-f12 text-gray-400">
        <span>0</span>
        <input
          type="range"
          min="0"
          max="100"
          value={sliderPercentage}
          onChange={(e) => handleSliderChange(parseInt(e.target.value))}
          className="w-full cursor-pointer  outline-none appearance-none value-slider"
        />
        <span>100%</span>
      </div>

      {/* Order Value */}
      <div className="relative">
        <label className="text-f12 text-gray-400">Order Value</label>
        <div className="text-f12 text-white absolute bottom-[28px] right-3">
          {quoteCurrency}
        </div>
        <input
          type="text"
          value={orderValue}
          onChange={(e) => handleOrderValueChange(e.target.value)}
          className={`w-full dark:bg-black dark:text-white text-sm rounded px-3 py-2 mt-1 outline-none border !border-red-500
             ${errors.orderValue ? '!border-red-500' : orderValue && parseFloat(orderValue) > 0 ? '!border-gray-600' : '!border-transparent'} `}
          placeholder="0"
        />
        <div className="text-f12 dark:text-gray-400 mt-1">
          Min: {symbolDetails?.minFunds || MIN_PRICE_ORDER_IN_USD} {quoteCurrency}
        </div>
      </div>
      {errors.orderValue && (
        <div className="text-red-500 text-f12 mt-1">{errors.orderValue}</div>
      )}
      <div className="hidden border-2 border-red-500"></div>
    </>
  );
};

export default LimitOrderForm;
